// Shared role metadata for the role hierarchy (mirrors backend rbac.py).
export const ROLE_RANK = {
  viewer: 0,
  analyst: 1,
  manager: 2,
  customer_admin: 3,
  partner: 4,
  eazy_developer: 5,
  eazy_admin: 6,
  super_admin: 7,
};

const LEGACY_MAP = { member: "viewer", admin: "super_admin", editor: "manager" };

export const ROLE_LABELS = {
  viewer: "Viewer",
  analyst: "Analyst",
  manager: "Manager",
  customer_admin: "Customer Admin",
  partner: "Partner",
  eazy_developer: "Eazy Developer",
  eazy_admin: "Eazy Admin",
  super_admin: "Super Admin",
};

// Item 24: how each role reads on the tenant side.
export const ROLE_HINTS = {
  viewer: "Tenant Viewer — only the dashboards shared with them",
  analyst: "Builds their own content inside the tenant",
  manager: "Tenant Developer — manages the tenant's datasets & dashboards",
  customer_admin: "Tenant Admin — people, permissions, logs & usage for their tenant",
  partner: "Oversees their tenant and its child tenants",
  eazy_developer: "Eazy staff — builds sources & dashboards inside any tenant",
  eazy_admin: "Eazy staff — administers every tenant",
  super_admin: "Platform owner",
};

// Ordered list (lowest -> highest) for selects.
export const ROLE_OPTIONS = [
  "viewer", "analyst", "manager", "customer_admin", "partner", "eazy_developer", "eazy_admin", "super_admin",
];

export const EAZY_STAFF_ROLES = ["eazy_developer", "eazy_admin"];

export function normalizeRole(role) {
  if (ROLE_RANK[role] !== undefined) return role;
  return LEGACY_MAP[role] || "viewer";
}

export function roleRank(role) {
  return ROLE_RANK[normalizeRole(role)] ?? 0;
}

export function roleLabel(role) {
  return ROLE_LABELS[normalizeRole(role)] || normalizeRole(role);
}

export function isTenantAdmin(role) {
  return roleRank(role) >= ROLE_RANK.customer_admin;
}

export function isSuperAdmin(role) {
  return normalizeRole(role) === "super_admin";
}

// Eazy-side staff: reaches every tenant.
export function isEazyStaff(role) {
  return EAZY_STAFF_ROLES.includes(normalizeRole(role));
}

// May act inside any tenant (Super Admin + Eazy staff).
export function isCrossTenant(role) {
  return isSuperAdmin(role) || isEazyStaff(role);
}

// Platform-wide administration (Super Admin or Eazy Admin).
export function isPlatformAdmin(role) {
  return ["super_admin", "eazy_admin"].includes(normalizeRole(role));
}

export function canManageUsers(role) {
  return isTenantAdmin(role) && normalizeRole(role) !== "eazy_developer";
}

export function isPartnerOrAbove(role) {
  return roleRank(role) >= ROLE_RANK.partner;
}

// Roles a given actor may assign (strictly below their own; super_admin can assign any).
export function assignableRoles(actorRole) {
  if (isSuperAdmin(actorRole)) return ROLE_OPTIONS;
  return ROLE_OPTIONS.filter((r) => ROLE_RANK[r] < roleRank(actorRole));
}
